import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import { BrandLogo, Button, Checkbox, GoogleSignInButton, Link, PasswordInput, Stack, TextInput } from '@fluent/ui';
import { supabase } from '../lib/supabase.js';
import { supabaseAuthErrorKey } from '../auth/errors.js';
import { signInWithGoogle as signInWithGoogleEverywhere } from '../auth/googleSignIn.js';
import { useUrlAuthError } from '../auth/useUrlAuthError.js';
import { LEGAL_URLS, LegalLinks } from '../legal/LegalLinks.js';
import { trackMetaRegistration } from '../lib/metaPixel.js';
import { isInAppBrowser } from '../pwa/installPromptStore.js';
import './auth.css';

export function SignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [agreed, setAgreed] = useState(false);

  const [errors, setErrors] = useState<{ email?: string; password?: string; terms?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  // Facebook / Instagram / TikTok open ad links in their own browser, and
  // Google refuses to sign anyone in there (403 disallowed_useragent). An ad
  // visitor who taps the Google button just hits an error page — so in those
  // browsers the button is gone and email is the way in (2026-10-05: the
  // first Facebook ad got 58 visits and no sign-ups).
  const [inApp] = useState(isInAppBrowser);

  // Surface Supabase / Google OAuth errors that came back via URL params.
  const oauthError = useUrlAuthError();
  useEffect(() => {
    if (oauthError) setErrors((prev) => ({ ...prev, form: oauthError }));
  }, [oauthError]);

  const validate = () => {
    const e: typeof errors = {};
    if (!email) e.email = t('auth.errors.email_required');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = t('auth.errors.invalid_email');
    if (!password) e.password = t('auth.errors.password_required');
    else if (password.length < 8) e.password = t('auth.errors.password_too_short');
    if (!agreed) e.terms = t('auth.errors.terms_required');
    return e;
  };

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // No display name is collected: Google sign-ups never provide one
        // either, and Home already falls back to the email's first part. One
        // field fewer on the slowest step of the funnel (audit F120).
        emailRedirectTo: `${window.location.origin}/`,
      },
    });
    setSubmitting(false);

    if (error) {
      setErrors({ form: t(supabaseAuthErrorKey(error)) });
      return;
    }
    // Counted for the Meta pixel HERE, not at the first sign-in (AuthProvider
    // skips email sign-ins). An already-registered email comes back with no
    // identities and is not a new account.
    if (data.user && (data.user.identities?.length ?? 0) > 0) {
      trackMetaRegistration(data.user.id, data.user.created_at);
    }
    // Email confirmation is off (DECISIONS 2026-10-05): sign-up returns a
    // session and the new learner goes straight in — home sends them on to
    // onboarding. The check-email screen stays for the day it is turned back on.
    if (data.session) {
      navigate('/', { replace: true });
      return;
    }
    navigate(`/check-email?email=${encodeURIComponent(email)}`, { replace: true });
  };

  const signInWithGoogle = async () => {
    setErrors((prev) => ({ ...prev, form: undefined }));
    const errorMessage = await signInWithGoogleEverywhere('signup');
    if (errorMessage) {
      setErrors((prev) => ({ ...prev, form: errorMessage }));
    }
  };

  return (
    <main className="auth-screen">
      <form className="auth-card" onSubmit={submit} noValidate>
        <BrandLogo className="auth-card__brand" />
        <h1 className="auth-card__title">{t('auth.signup.title')}</h1>
        <p className="auth-card__subtitle">{t('auth.signup.subtitle')}</p>

        {inApp ? (
          <p className="auth-card__hint">{t('auth.signup.in_app_note')}</p>
        ) : (
          <>
            <GoogleSignInButton onClick={signInWithGoogle}>{t('auth.signup.google')}</GoogleSignInButton>
            <p className="auth-card__hint">{t('auth.signup.google_hint')}</p>

            <span className="auth-card__divider">ან</span>
          </>
        )}

        <Stack gap={4}>
          <TextInput
            label={t('auth.signup.email_label')}
            placeholder={t('auth.signup.email_placeholder')}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email}
            required
          />
          <PasswordInput
            label={t('auth.signup.password_label')}
            hint={t('auth.signup.password_hint')}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            required
          />
          <Checkbox
            label={
              <Trans
                i18nKey="auth.signup.terms_label_with_links"
                components={{
                  termsLink: <Link href={LEGAL_URLS.terms} onClick={(e) => e.stopPropagation()} />,
                  privacyLink: <Link href={LEGAL_URLS.privacy} onClick={(e) => e.stopPropagation()} />,
                }}
              />
            }
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            error={Boolean(errors.terms)}
          />
          {errors.terms && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-xs)', fontWeight: 700 }}>
              {errors.terms}
            </span>
          )}
          {errors.form && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', fontWeight: 700 }}>
              {errors.form}
            </span>
          )}
          {/* neutral, not primary: on THIS screen Google is the fast path
              (no email confirmation), so it keeps the loud button — except
              where Google can't work and email is the only way in. */}
          <Button type="submit" variant={inApp ? 'primary' : 'neutral'} size="lg" fullWidth loading={submitting}>
            {submitting ? t('auth.signup.submit_loading') : t('auth.signup.submit')}
          </Button>
        </Stack>

        <p className="auth-card__alt">
          {t('auth.signup.have_account')}
          <RouterLink to="/login">{t('auth.signup.login_link')}</RouterLink>
        </p>
      </form>
      <div className="auth-screen__legal">
        <LegalLinks />
      </div>
    </main>
  );
}

